/**
 * PWA install funnel — vanilla TS controller.
 *
 * Translates the React `useInstallPrompt` pattern from
 * `neveralone-chs-org/never-alone-pwa` into a framework-free module.
 *
 * Two surfaces, one controller:
 * 1. Chrome / Edge / Android — the browser fires `beforeinstallprompt`;
 *    we stash the event and fire it on demand from a "INSERT TO POCKET"
 *    button on the cabinet.
 * 2. iOS Safari — Apple does NOT implement `beforeinstallprompt`. We
 *    detect iOS Safari via UA and surface a manual "Add to Home Screen"
 *    overlay walking the user through Safari's share sheet.
 *
 * Once installed (matchMedia('(display-mode: standalone)') OR iOS
 * `navigator.standalone === true`), neither surface re-appears.
 *
 * Dismissed state lives in `localStorage` for now. Will migrate to
 * IndexedDB-backed `StorageAPI` once that lands under epic:runtime —
 * single boolean, 5-line swap.
 */

const DISMISSED_KEY = 'arcadia84:install-dismissed-v1';
const IOS_INSTRUCTIONS_DISMISSED_KEY = 'arcadia84:ios-instructions-dismissed-v1';

/**
 * The browser's deferred install prompt event. Standardised on
 * Chrome/Edge/Android; not in any TS lib type yet, hence the local shape.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

/** What the cabinet should show in response to the current install state. */
export type InstallSurface =
  /** Browser-fired install prompt available — show the in-cabinet banner. */
  | 'banner'
  /** iOS Safari — surface the "Add to Home Screen" overlay affordance. */
  | 'ios-instructions'
  /** Already installed (standalone / iOS standalone) — hide everything. */
  | 'already-installed'
  /** User dismissed the funnel — hide everything until reset. */
  | 'dismissed'
  /** Browser doesn't support install AND isn't iOS — nothing to show. */
  | 'unsupported';

export interface InstallStatus {
  surface: InstallSurface;
  /** True when running as an installed PWA (iOS standalone OR display-mode standalone). */
  isStandalone: boolean;
  /** True when the UA looks like iOS Safari and we're not standalone. */
  isIosSafari: boolean;
  /** True when a `beforeinstallprompt` event has been captured + not yet consumed. */
  hasDeferredPrompt: boolean;
}

/** Outcome of an install attempt. */
export type InstallOutcome =
  | 'accepted'
  | 'dismissed'
  | 'unavailable'
  | 'ios-instructions';

export interface InstallController {
  /** Current status. Cheap to call; reflects the latest event state. */
  getStatus(): InstallStatus;
  /**
   * Trigger the install funnel. On Chrome/Android invokes the deferred
   * prompt and resolves with the user's choice. On iOS Safari resolves
   * with `'ios-instructions'` so the caller knows to show the overlay.
   * Anywhere else resolves with `'unavailable'`.
   */
  install(): Promise<InstallOutcome>;
  /** Mark the funnel dismissed. Persists across reloads. */
  dismiss(): void;
  /** Reset the dismissed flag (used by tests + a future settings reset). */
  reset(): void;
  /** Subscribe to status changes. Returns an unsubscribe fn. */
  subscribe(cb: (status: InstallStatus) => void): () => void;
  /** Tear down listeners. Idempotent. */
  destroy(): void;
}

/**
 * Optional dependency injection for testing.
 */
export interface InstallDeps {
  window?: Window;
  navigator?: Navigator;
  storage?: Storage;
}

/**
 * Build an install controller bound to the supplied window/navigator.
 * Production callers pass nothing; tests inject happy-dom or stubs.
 */
export function createInstallController(
  deps: InstallDeps = {}
): InstallController {
  const win = deps.window ?? globalThis.window;
  const nav = deps.navigator ?? win.navigator;
  const storage = deps.storage ?? win.localStorage;

  let deferredPrompt: BeforeInstallPromptEvent | null = null;
  let destroyed = false;
  const subscribers = new Set<(status: InstallStatus) => void>();

  const handleBeforeInstallPrompt = (e: Event): void => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  };

  const handleAppInstalled = (): void => {
    deferredPrompt = null;
    notify();
  };

  win.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  win.addEventListener('appinstalled', handleAppInstalled);

  function isStandalone(): boolean {
    // Chrome/Edge/Firefox standalone detection.
    if (typeof win.matchMedia === 'function') {
      try {
        if (win.matchMedia('(display-mode: standalone)').matches) {
          return true;
        }
      } catch {
        // matchMedia can throw in non-browser environments — fall through.
      }
    }
    // iOS Safari standalone — non-standard prop on navigator.
    const iosStandalone = (nav as Navigator & { standalone?: boolean }).standalone;
    return iosStandalone === true;
  }

  function isIosSafari(): boolean {
    const ua = nav.userAgent || '';
    const isIosDevice = /iPad|iPhone|iPod/.test(ua);
    if (!isIosDevice) {
      return false;
    }
    // Exclude Chrome on iOS (it's WebKit but doesn't get A2HS the same way).
    // Chrome iOS UA contains "CriOS"; Firefox iOS contains "FxiOS".
    if (/CriOS|FxiOS|EdgiOS/.test(ua)) {
      return false;
    }
    return true;
  }

  function isDismissed(): boolean {
    try {
      return storage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  function persistDismiss(key: string): void {
    try {
      storage.setItem(key, '1');
    } catch {
      // Storage can throw in private mode — swallow; dismissal becomes
      // ephemeral, which is acceptable.
    }
  }

  function persistReset(): void {
    try {
      storage.removeItem(DISMISSED_KEY);
      storage.removeItem(IOS_INSTRUCTIONS_DISMISSED_KEY);
    } catch {
      /* swallow */
    }
  }

  function getStatus(): InstallStatus {
    const standalone = isStandalone();
    const ios = !standalone && isIosSafari();
    const dismissed = isDismissed();
    const hasDeferred = deferredPrompt !== null;

    let surface: InstallSurface;
    if (standalone) {
      surface = 'already-installed';
    } else if (dismissed) {
      surface = 'dismissed';
    } else if (hasDeferred) {
      surface = 'banner';
    } else if (ios) {
      // Don't auto-show iOS overlay if the user already dismissed it.
      const iosDismissed =
        (() => {
          try {
            return storage.getItem(IOS_INSTRUCTIONS_DISMISSED_KEY) === '1';
          } catch {
            return false;
          }
        })();
      surface = iosDismissed ? 'dismissed' : 'ios-instructions';
    } else {
      surface = 'unsupported';
    }

    return {
      surface,
      isStandalone: standalone,
      isIosSafari: ios,
      hasDeferredPrompt: hasDeferred,
    };
  }

  function notify(): void {
    if (destroyed) {
      return;
    }
    const status = getStatus();
    for (const cb of subscribers) {
      cb(status);
    }
  }

  return {
    getStatus,

    async install(): Promise<InstallOutcome> {
      if (destroyed) {
        return 'unavailable';
      }
      const status = getStatus();
      if (status.surface === 'already-installed') {
        return 'unavailable';
      }
      if (status.surface === 'banner' && deferredPrompt) {
        const evt = deferredPrompt;
        deferredPrompt = null;
        notify();
        await evt.prompt();
        const choice = await evt.userChoice;
        return choice.outcome;
      }
      if (status.isIosSafari) {
        return 'ios-instructions';
      }
      return 'unavailable';
    },

    dismiss(): void {
      const status = getStatus();
      if (status.isIosSafari && !status.hasDeferredPrompt) {
        // iOS path — use the iOS-specific dismiss key so a future
        // beforeinstallprompt event (e.g., user switches browsers)
        // isn't suppressed.
        persistDismiss(IOS_INSTRUCTIONS_DISMISSED_KEY);
      } else {
        persistDismiss(DISMISSED_KEY);
      }
      notify();
    },

    reset(): void {
      persistReset();
      notify();
    },

    subscribe(cb): () => void {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },

    destroy(): void {
      if (destroyed) {
        return;
      }
      destroyed = true;
      win.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      win.removeEventListener('appinstalled', handleAppInstalled);
      subscribers.clear();
      deferredPrompt = null;
    },
  };
}
